<template>
  <div
    :class="['email-component', 'customer-responses-component', { selected }]"
    :style="computedStyles"
    @click="handleClick"
  >
    <div class="customer-responses-wrapper">
      <!-- Component Header -->
      <div v-if="componentData.showTitle" class="responses-header" :style="headerStyles">
        <h3>{{ getLabel('title') }}</h3>
      </div>

      <!-- Responses List -->
      <div class="responses-list">
        <div
          v-for="(item, index) in displayResponses"
          :key="index"
          class="response-item"
          :style="getItemStyles(index)"
        >
          <!-- Question -->
          <div class="response-question" :style="questionStyles">
            {{ item.question }}
          </div>

          <!-- Answer -->
          <div class="response-answer" :style="answerStyles">
            <template v-if="item.type === 'checkbox'">
              <span v-if="item.answer === 'Yes' || item.answer === true">
                ✓ {{ getLabel('yes') }}
              </span>
              <span v-else>
                ✗ {{ getLabel('no') }}
              </span>
            </template>
            <template v-else-if="item.type === 'file'">
              <span class="file-indicator">
                📎 {{ item.answer || getLabel('fileAttached') }}
              </span>
            </template>
            <template v-else-if="item.type === 'signature'">
              <span class="signature-indicator">
                ✍️ {{ getLabel('signatureProvided') }}
              </span>
            </template>
            <template v-else>
              {{ item.answer || getLabel('noResponse') }}
            </template>
          </div>

          <!-- Policies/Links if any -->
          <div v-if="item.policies && item.policies.length > 0" class="response-policies" :style="policyStyles">
            <span v-for="(policy, pIndex) in item.policies" :key="pIndex" class="policy-link">
              {{ policy.policy_link_text }}
              <span v-if="pIndex < item.policies.length - 1"> • </span>
            </span>
          </div>

          <div v-if="item.external_links && item.external_links.length > 0" class="response-links" :style="linkStyles">
            <span v-for="(link, lIndex) in item.external_links" :key="lIndex" class="external-link">
              {{ link.link_text }}
              <span v-if="lIndex < item.external_links.length - 1"> • </span>
            </span>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="displayResponses.length === 0" class="empty-state" :style="emptyStateStyles">
        {{ getLabel('noResponses') }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomerResponsesComponent',
  props: ['componentData', 'selected'],
  data() {
    return {
      // Sample data for the builder preview
      sampleResponses: [
        {
          question: 'Will you need wheelchair access?',
          answer: 'Yes',
          type: 'checkbox'
        },
        {
          question: 'Emergency contact phone number',
          answer: '+1 (555) 123-4567',
          type: 'phone'
        }
      ],

      labels: {
        english: {
          title: 'Customer Information',
          yes: 'Yes',
          no: 'No',
          noResponse: 'No response provided',
          noResponses: 'No customer responses available',
          fileAttached: 'File attached',
          signatureProvided: 'Signature provided'
        },
        spanish: {
          title: 'Información del cliente',
          yes: 'Sí',
          no: 'No',
          noResponse: 'Sin respuesta',
          noResponses: 'No hay respuestas del cliente disponibles',
          fileAttached: 'Archivo adjunto',
          signatureProvided: 'Firma proporcionada'
        },
        french: {
          title: 'Informations client',
          yes: 'Oui',
          no: 'Non',
          noResponse: 'Aucune réponse fournie',
          noResponses: 'Aucune réponse client disponible',
          fileAttached: 'Fichier joint',
          signatureProvided: 'Signature fournie'
        },
        swedish: {
          title: 'Kundinformation',
          yes: 'Ja',
          no: 'Nej',
          noResponse: 'Inget svar angivet',
          noResponses: 'Inga kundsvar tillgängliga',
          fileAttached: 'Fil bifogad',
          signatureProvided: 'Signatur tillhandahållen'
        },
        danish: {
          title: 'Kundeoplysninger',
          yes: 'Ja',
          no: 'Nej',
          noResponse: 'Intet svar angivet',
          noResponses: 'Ingen kundesvar tilgængelige',
          fileAttached: 'Fil vedhæftet',
          signatureProvided: 'Underskrift afgivet'
        },
        greek: {
          title: 'Πληροφορίες πελάτη',
          yes: 'Ναι',
          no: 'Όχι',
          noResponse: 'Δεν δόθηκε απάντηση',
          noResponses: 'Δεν υπάρχουν διαθέσιμες απαντήσεις πελατών',
          fileAttached: 'Συνημμένο αρχείο',
          signatureProvided: 'Παρέχεται υπογραφή'
        }
      }
    }
  },
  computed: {
    currentLanguage() {
      return this.componentData.language || 'english'
    },

    displayResponses() {
      // In production, this would use actual data from componentData.responses
      // For the builder, show sample data if showSampleData is true
      if (this.componentData.showSampleData !== false) {
        // Filter responses based on settings
        let responses = [...this.sampleResponses]

        if (this.componentData.maxResponses && this.componentData.maxResponses > 0) {
          responses = responses.slice(0, this.componentData.maxResponses)
        }

        if (this.componentData.excludeEmpty) {
          responses = responses.filter(r => r.answer && r.answer.trim() !== '')
        }

        return responses
      }

      return this.componentData.responses || []
    },

    computedStyles() {
      const styles = this.componentData.styles || {}
      const backgroundColor = styles.backgroundColor === 'inherit' || !styles.backgroundColor ?
        'transparent' : styles.backgroundColor

      return {
        backgroundColor: backgroundColor,
        padding: `${styles.paddingTop || '20'}px ${styles.paddingRight || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingLeft || '20'}px`,
        marginTop: `${styles.marginTop || '16'}px`,
        marginBottom: `${styles.marginBottom || '16'}px`,
        borderRadius: `${styles.borderRadius || '8'}px`,
        border: styles.showBorder ? `1px solid ${styles.borderColor || '#e5e7eb'}` : 'none',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    headerStyles() {
      const styles = this.componentData.styles || {}
      return {
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: styles.showDividers ? `1px solid ${styles.dividerColor || '#e5e7eb'}` : 'none'
      }
    },

    questionStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.questionFontSize || '13'}px`,
        fontWeight: styles.questionFontWeight || '600',
        color: styles.questionColor || '#4b5563',
        marginBottom: '4px',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    answerStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.answerFontSize || '14'}px`,
        fontWeight: styles.answerFontWeight || '400',
        color: styles.answerColor || '#111827',
        lineHeight: '1.5',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    policyStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.metaFontSize || '11'}px`,
        color: styles.metaColor || '#6b7280',
        marginTop: '4px',
        fontStyle: 'italic'
      }
    },

    linkStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.metaFontSize || '11'}px`,
        color: styles.linkColor || '#2563eb',
        marginTop: '4px'
      }
    },

    emptyStateStyles() {
      const styles = this.componentData.styles || {}
      return {
        padding: '24px',
        textAlign: 'center',
        color: styles.metaColor || '#9ca3af',
        fontSize: `${styles.fontSize || '14'}px`,
        fontStyle: 'italic'
      }
    }
  },
  methods: {
    handleClick() {
      this.$emit('click')
    },

    getLabel(key) {
      const languageLabels = this.labels[this.currentLanguage] || this.labels.english
      return languageLabels[key] || key
    },

    getItemStyles(index) {
      const styles = this.componentData.styles || {}
      const isLast = index === this.displayResponses.length - 1

      return {
        paddingTop: index === 0 ? '0' : '12px',
        paddingBottom: '12px',
        borderBottom: !isLast ? `1px solid ${styles.itemDividerColor || '#f3f4f6'}` : 'none',
        marginBottom: isLast ? '0' : '0'
      }
    }
  }
}
</script>

<style scoped>
.customer-responses-component {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  width: 100%;
  box-sizing: border-box;
}

.customer-responses-component:hover,
.customer-responses-component.selected {
  border-color: #e14eca !important;
  box-shadow: 0 0 0 4px rgba(225, 78, 202, 0.1);
}

.customer-responses-wrapper {
  width: 100%;
}

.responses-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.responses-list {
  width: 100%;
}

.response-item {
  width: 100%;
}

.response-question {
  width: 100%;
  word-wrap: break-word;
}

.response-answer {
  width: 100%;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.response-policies,
.response-links {
  width: 100%;
}

.policy-link,
.external-link {
  display: inline;
}

.file-indicator,
.signature-indicator {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.empty-state {
  width: 100%;
}

@media (max-width: 600px) {
  .response-question {
    font-size: 12px !important;
  }

  .response-answer {
    font-size: 13px !important;
  }
}
</style>
